(() => {
class TmSizingFlow extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.form = document.getElementById(this.dataset.formId);
    if (!this.form) return;

    this.productRoot = this.closest('[data-product-root]');

    this.sortStandardSizes();

    this.modeInputs = Array.from(this.querySelectorAll('[data-tm-sizing-mode]'));
    this.standardInputs = Array.from(this.querySelectorAll('[data-tm-standard-size]'));
    this.customInputs = Array.from(this.querySelectorAll('[data-tm-custom-field]'));
    this.unitInputs = Array.from(this.querySelectorAll('[data-tm-unit]'));

    // Automatic measurement conversion
    this.currentUnit =
      this.unitInputs.find((input) => input.checked)?.value || 'cm';


    this.requiredMeasurements = Array.from(
      this.querySelectorAll('[data-tm-required-measurement]')
    );
    this.validationMessage = this.querySelector('[data-tm-sizing-validation]');
    this.progressText = this.querySelector('[data-tm-progress-text]');
    this.progressBar = this.querySelector('[data-tm-progress-bar]');

    this.onInput = this.update.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);

    this.addEventListener('change', this.onInput);
    this.addEventListener('input', this.onInput);
    this.form.addEventListener('submit', this.onSubmit, true);

    if (this.productRoot) {
      this.observer = new MutationObserver(() => this.updateButtonState());
      this.observer.observe(this.productRoot, { childList: true, subtree: true });
    }

    this.update();
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.removeEventListener('change', this.onInput);
    this.removeEventListener('input', this.onInput);
    this.form?.removeEventListener('submit', this.onSubmit, true);
    this.observer?.disconnect();
    this.initialized = false;
  }

  sortStandardSizes() {
    const grid = this.querySelector('.tm-sizing__size-grid');
    if (!grid) return;

    const options = Array.from(
      grid.querySelectorAll('.tm-sizing__size-option')
    );

    if (options.length < 2) return;

    const getNumericValue = (option) => {
      const input = option.querySelector('[data-tm-standard-size]');
      const rawValue = input?.value?.trim() || '';

      // Only treat purely numeric sizes as numbers.
      // Non-numeric sizes keep their existing position.
      if (!/^\d+(?:\.\d+)?$/.test(rawValue)) {
        return null;
      }

      return Number(rawValue);
    };

    const numericOptions = options
      .map((option, index) => ({
        option,
        index,
        value: getNumericValue(option)
      }))
      .filter((item) => item.value !== null)
      .sort((a, b) => {
        return a.value - b.value || a.index - b.index;
      })
      .map((item) => item.option);

    let numericIndex = 0;

    const orderedOptions = options.map((option) => {
      if (getNumericValue(option) === null) {
        return option;
      }

      return numericOptions[numericIndex++];
    });

    orderedOptions.forEach((option) => {
      grid.appendChild(option);
    });
  }

  get selectedMode() {
    return this.modeInputs.find((input) => input.checked)?.dataset.tmSizingMode;
  }

  // Automatic measurements conversion part 2
  update(event) {
    const changedUnit = event?.target?.matches?.('[data-tm-unit]')
      ? event.target
      : null;

    if (changedUnit?.checked) {
      const nextUnit = changedUnit.value;

      if (nextUnit !== this.currentUnit) {
        const previousUnit = this.currentUnit;
        this.currentUnit = nextUnit;
        this.convertMeasurements(previousUnit, nextUnit);
      }
    }

    const mode = this.selectedMode;

    this.querySelectorAll('[data-tm-sizing-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.tmSizingPanel !== mode;
    });

    this.standardInputs.forEach((input, index) => {
      input.disabled = mode !== 'standard';
      input.required = mode === 'standard' && index === 0;
    });

    [...this.customInputs, ...this.unitInputs].forEach((input) => {
      input.disabled = mode !== 'custom';
    });

    this.updateUnitLabels();
    this.updateProgress();
    this.updateValidationMessage();
    this.updateButtonState();
  }

  // Automatic measurements conversion part 3
  convertMeasurements(fromUnit, toUnit) {
    if (!fromUnit || !toUnit || fromUnit === toUnit) return;

    let factor;

    if (fromUnit === 'cm' && toUnit === 'inches') {
      factor = 1 / 2.54;
    } else if (fromUnit === 'inches' && toUnit === 'cm') {
      factor = 2.54;
    } else {
      return;
    }

    this.customInputs.forEach((input) => {
      if (input.value.trim() === '') return;

      const value = Number.parseFloat(input.value);
      if (!Number.isFinite(value)) return;

      const converted = Math.round(value * factor * 10) / 10;
      input.value = converted.toString();

      input.dispatchEvent(
        new Event('input', { bubbles: true })
      );

      input.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    });
  }

  updateUnitLabels() {
    const unit = this.unitInputs.find((input) => input.checked)?.value || 'cm';
    this.querySelectorAll('[data-tm-unit-label]').forEach((label) => {
      label.textContent = unit;
    });
  }

  updateProgress() {
    if (!this.progressText || !this.progressBar) return;

    const total = this.requiredMeasurements.length;
    const complete = this.requiredMeasurements.filter(
      (input) => input.value.trim() !== '' && input.checkValidity()
    ).length;
    const progress = total > 0 ? complete / total : 0;

    this.progressText.textContent = `${complete} of ${total} required measurements`;
    this.progressBar.style.transform = `scaleX(${progress})`;
  }

  sizingIsValid() {
    const mode = this.selectedMode;

    if (mode === 'standard') {
      return this.standardInputs.some((input) => input.checked && !input.disabled);
    }

    if (mode === 'custom') {
      return this.requiredMeasurements.every(
        (input) => !input.disabled && input.value.trim() !== '' && input.checkValidity()
      );
    }

    return false;
  }

  updateValidationMessage(isError = false) {
    if (!this.validationMessage) return;

    const mode = this.selectedMode;
    const valid = this.sizingIsValid();
    let message = 'Choose standard sizing or made to your measurements to continue.';

    if (mode === 'standard' && !valid) {
      message = 'Select a standard size to continue.';
    } else if (mode === 'custom' && !valid) {
      const remaining = this.requiredMeasurements.filter(
        (input) => input.value.trim() === '' || !input.checkValidity()
      ).length;
      message = `Complete ${remaining} required ${remaining === 1 ? 'measurement' : 'measurements'} to continue.`;
    } else if (valid) {
      message = 'Sizing details ready to add to your order.';
    }

    this.validationMessage.textContent = message;
    this.validationMessage.dataset.state = isError && !valid ? 'error' : valid ? 'complete' : 'incomplete';
  }

  currentVariantIsAvailable() {
    if (!this.productRoot) return true;

    const variantScript = this.productRoot.querySelector('[data-current-variant]');
    if (!variantScript) return false;

    try {
      return Boolean(JSON.parse(variantScript.textContent).available);
    } catch (_error) {
      return false;
    }
  }

  updateButtonState() {
    if (!this.form) return;

    const button = this.form.querySelector('.add-to-cart');
    if (!button) return;

    const unavailable = !this.currentVariantIsAvailable();
    const sizingIncomplete = !this.sizingIsValid();
    const transient = button.classList.contains('disabled--transient');
    const shouldDisable = unavailable || sizingIncomplete || transient;

    button.disabled = shouldDisable;
    button.classList.toggle('tm-sizing-incomplete', sizingIncomplete);
    button.setAttribute('aria-describedby', this.validationMessage?.id || '');
  }

  handleSubmit(event) {
    if (this.sizingIsValid()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.updateValidationMessage(true);

    const firstInvalid = this.selectedMode
      ? this.querySelector(
          '[data-tm-standard-size]:enabled:invalid, [data-tm-required-measurement]:enabled:invalid'
        )
      : this.modeInputs[0];
    firstInvalid?.focus();
  }
}

if (!customElements.get('tm-sizing-flow')) {
  customElements.define('tm-sizing-flow', TmSizingFlow);
}
})();
